import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { legacySyncQueryRootKey } from "../config";
import { DEFAULT_LEGACY_SYNC_ENDPOINT_PREFIX, executeLegacySyncTarget, fetchLegacySyncRunResult,
  fetchLegacySyncTargets, pauseLegacySyncRun, previewLegacySyncTarget } from "../repo/legacySyncRepo";
import type { LegacySyncEndpointPrefix, LegacySyncMode, LegacySyncOptions, LegacySyncRunResult } from "../types";

export const PREVIEW_SLOW_NOTICE_MS = 10_000;

/** 时间范围预览与冻结计划状态；轮询失败保留证据，不自动再次执行。 */
export function useLegacySyncRuntime(endpointPrefix: LegacySyncEndpointPrefix = DEFAULT_LEGACY_SYNC_ENDPOINT_PREFIX) {
  const [latestResult, setLatestResult] = useState<LegacySyncRunResult>();
  const latest = useRef<LegacySyncRunResult | undefined>(undefined);
  const results = useRef(new Map<string, LegacySyncRunResult>());
  const [actionErrorMessage, setError] = useState("");
  const [previewingTargetKey, setPreviewing] = useState<string>();
  const [submittingTargetKey, setSubmitting] = useState<string>();
  const [refreshingResult, setRefreshing] = useState(false);
  const [slowPreviewTargetKey, setSlow] = useState<string>();
  const busy = useRef(false);
  const generation = useRef(0);
  const query = useQuery({ queryKey: [...legacySyncQueryRootKey, endpointPrefix],
    queryFn: () => fetchLegacySyncTargets(endpointPrefix), retry: 1, refetchOnWindowFocus: false });

  /** 范围变动或重新预览后撤销旧计划，禁止误执行上一个时间范围。 */
  function clearPlan(targetKey: string) {
    generation.current += 1;
    results.current.delete(targetKey);
    if (latest.current?.targetKey === targetKey) {
      latest.current = undefined;
      setLatestResult(undefined);
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("syncRun");
    window.history.replaceState(null, "", url);
  }

  useEffect(() => {
    if (latestResult?.status === "completed" && latestResult.cursorTo) void query.refetch();
  }, [latestResult?.status, latestResult?.runId, latestResult?.cursorTo, query.refetch]);

  /** 保存不含凭据的运行链接，刷新后仍可恢复查看。 */
  function accept(result: LegacySyncRunResult) {
    results.current.set(result.targetKey, result);
    latest.current = result;
    setLatestResult(result);
    if (result.runId) {
      const url = new URL(window.location.href);
      url.searchParams.set("syncRun", result.runId);
      window.history.replaceState(null, "", url);
    }
  }

  useEffect(() => {
    const runId = new URLSearchParams(window.location.search).get("syncRun");
    const current = generation.current;
    let active = true;
    if (runId) void fetchLegacySyncRunResult(runId, endpointPrefix).then((result) => {
        if (active && generation.current === current) { results.current.set(result.targetKey, result); latest.current = result; setLatestResult(result); }
    }).catch(() => { if (active && generation.current === current) setError("恢复运行结果失败，请按运行编号重新查看。"); });
    return () => { active = false; generation.current += 1; };
  }, [endpointPrefix]);

  useEffect(() => {
    if (latestResult?.status !== "started" || !latestResult.runId) return;
    let active = true;
    const runId = latestResult.runId;
    const timer = window.setTimeout(() => {
      void fetchLegacySyncRunResult(runId, endpointPrefix).then((result) => {
        if (active && latest.current?.runId === runId) { results.current.set(result.targetKey, result); latest.current = result; setLatestResult(result); }
      }).catch(() => {
        if (active) setError("查询暂时失败；后台状态未确认，请刷新结果，不要重复执行。");
      });
    }, 3000);
    return () => { active = false; window.clearTimeout(timer); };
  }, [endpointPrefix, latestResult]);

  const running = latestResult?.status === "started";
  return {
    targets: query.data?.items ?? [], loading: query.isLoading,
    errorMessage: query.error ? "同步对象加载失败，请重试。" : actionErrorMessage,
    previewingTargetKey, executingTargetKey: submittingTargetKey ?? (running ? latestResult.targetKey : undefined),
    latestResult, slowPreviewTargetKey, refreshingResult,
    invalidatePreview(targetKey: string) {
      if (busy.current || latest.current?.status === "started") return;
      clearPlan(targetKey); setError("");
    },
    selectTargets(targetKeys: string[]) {
      if (busy.current || latest.current?.status === "started") return;
      const result = [...results.current.values()].reverse().find((item) => targetKeys.includes(item.targetKey));
      latest.current = result; setLatestResult(result); setError("");
    },
    async handlePreview(targetKey: string, syncMode: LegacySyncMode, options: LegacySyncOptions = {}) {
      if (busy.current || latest.current?.status === "started") return;
      clearPlan(targetKey);
      busy.current = true;
      const current = ++generation.current;
      setError(""); setPreviewing(targetKey);
      const timer = window.setTimeout(() => setSlow(targetKey), PREVIEW_SLOW_NOTICE_MS);
      try {
        const result = await previewLegacySyncTarget(targetKey, syncMode, endpointPrefix, options);
        if (generation.current === current) accept(result);
      } catch (error) { setError(error instanceof Error ? error.message : "有限预览失败"); }
      finally { window.clearTimeout(timer); setSlow(undefined); setPreviewing(undefined); busy.current = false; }
    },
    async handleExecute(targetKey: string, syncMode: LegacySyncMode) {
      if (busy.current || latest.current?.status === "started") return;
      const plan = latest.current;
      if (!plan?.planId || plan.targetKey !== targetKey || plan.syncMode !== syncMode
        || !["planned", "paused", "partial_failed"].includes(plan.status)) {
        setError("请先对同一对象、同一模式生成有限预览计划。"); return;
      }
      if (plan.summary.plannedUpdate > 0) {
        setError("此预览包含旧更新动作，请重新生成只新增预览。"); return;
      }
      busy.current = true; setSubmitting(targetKey); setError("");
      try {
        const result = await executeLegacySyncTarget(targetKey, syncMode, endpointPrefix, {
          planId: plan.planId, resumeRunId: plan.runId ?? undefined,
        });
        if (result.status === "blocked" && !result.runId) {
          // 明确未启动时保留原计划与数量；未知启动结果仍沿用既有查运行流程。
          setError(result.message);
        } else {
          accept(result);
        }
      } catch (error) { setError(error instanceof Error ? error.message : "启动结果未确认，请先查看原运行。"); }
      finally { busy.current = false; setSubmitting(undefined); }
    },
    async handlePause() {
      const current = latest.current;
      if (!current?.runId || current.status !== "started") return;
      try {
        const response = await pauseLegacySyncRun(current.runId, endpointPrefix);
        if (latest.current?.runId === current.runId && latest.current.status === "started") accept(response);
      } catch (error) { setError(error instanceof Error ? error.message : "暂停请求未确认，请刷新结果。"); }
    },
    async restoreRun(runId: string) {
      if (busy.current || latest.current?.status === "started") return;
      busy.current = true;
      setRefreshing(true);
      try { accept(await fetchLegacySyncRunResult(runId, endpointPrefix)); }
      catch (error) { setError(error instanceof Error ? error.message : "查询失败"); }
      finally { busy.current = false; setRefreshing(false); }
    },
    async refreshLatestResult() {
      const runId = latest.current?.runId;
      if (!runId) return;
      setRefreshing(true);
      try { accept(await fetchLegacySyncRunResult(runId, endpointPrefix)); setError(""); }
      catch { setError("查询失败，已保留最后进度；不能据此判断后台已停止。"); }
      finally { setRefreshing(false); }
    },
  };
}
